import { MarkerIcon } from "components/website/partners/shared/markers";
import { discoverPath } from "i18n/urlLocale";
import { forwardRef, useContext, type CSSProperties } from "react";
import { useHistory } from "react-router-dom";
import { GuideLangContext } from "../discoverSections";
import { useRouteMapUi, useTypeLabel } from "./copy";
import type { MapPoint } from "./data";
import { Check, Close, Globe, Plus } from "./icons";
import { markerLook } from "./mapModel";
import styles from "./PlaceSheet.module.scss";

interface PlaceSheetProps {
	point: MapPoint;
	countrySlug: string;
	/** the builder: absent on a phone, where there is none, and the card is the guide link alone */
	inRoute?: boolean;
	onToggle?: () => void;
	onClose: () => void;
}

/**
 * The card of the destination you chose, the demo's `#sheet`: a photo (or its type's icon on its tint) with a close, its type, name and
 * one line, and what to do with it: add it to my route (or take it out again) and open its guide. Over the map's bottom left on a
 * desktop, a sheet over the panel's foot on a phone.
 */
const PlaceSheet = forwardRef<HTMLDivElement, PlaceSheetProps>(({ point, countrySlug, inRoute, onToggle, onClose }, ref) => {
	const t = useRouteMapUi();
	const typeLabel = useTypeLabel();
	const lang = useContext(GuideLangContext);
	const history = useHistory();
	const look = markerLook(point.type);
	const href = discoverPath(`/discover/${countrySlug}/${point.slug}`, lang);
	return (
		<div ref={ref} className={styles.sheet} role="dialog" aria-label={point.name} style={{ "--tint": look.tint, "--ink": look.ink } as CSSProperties}>
			<div className={styles.hero}>
				{point.photo ? <img src={point.photo} alt={point.name} /> : <MarkerIcon name={look.icon} size={56} />}
				<button type="button" className={styles.close} onClick={onClose} aria-label={t("closeCard", "Close")}>
					<Close size={14} />
				</button>
			</div>
			<div className={styles.bd}>
				{point.type && (
					<div className={styles.meta}>
						<span className={styles.rmChip}>{typeLabel(point)}</span>
					</div>
				)}
				<h2>{point.name}</h2>
				{point.blurb && <p>{point.blurb}</p>}
				<div className={styles.acts}>
					{onToggle && (
						<button type="button" className={`${styles.btn} ${styles.btnGreen} ${inRoute ? styles.btnOn : ""}`} onClick={onToggle}>
							{inRoute ? <Check size={16} /> : <Plus size={16} color="#fff" weight={2.8} />}
							{inRoute ? t("inMyRoute", "In my route") : t("addToRoute", "Add to my route")}
						</button>
					)}
					<a
						className={styles.btn}
						href={href}
						onClick={(e) => {
							e.preventDefault();
							history.push(href);
						}}
					>
						<Globe size={16} />
						{t("guideBtn", "Guide")}
					</a>
				</div>
			</div>
		</div>
	);
});

export default PlaceSheet;
